import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import * as Comlink from "comlink";
import { type Bookmark, Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { store } from "app/store";
import {
  selectSnackbarOpen,
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import BackgroundLoadingIndicator from "components/background-loading-indicator";
import { NON_BREAKABLE_SPACE } from "constants/common";
import { MEDICAL_SCRIBE_FEATURE } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useAudioAnalyzer } from "features/medical-scribe/medical-scribe-recording-widget/use-audio-analyzer.hook";
import useCheckPaywallFeatureAvailability from "features/paywall/use-check-paywall-feature-availability.hook";
import { LAST_FOCUSED_WORD_PROCESSOR_FIELD } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import useSyncBetweenTabs, {
  type SyncMessage,
  appInstanceId,
  dispatchTabSyncMessage,
  syncChannel
} from "hooks/use-sync-between-tabs.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import {
  useCreateAmbientScribeMutation,
  useGenerateAmbientSoapNotesMutation
} from "services/ambient-scribe";
import { setFavicon } from "utils/set-favicon.util";

import {
  detectSuggestedBitrate,
  verifyDatabaseIntegrity
} from "./audio-helpers.util";
import MedicalScribeConfirmationDialog from "./components/medical-scribe-confirmation-dialog.component";
import { updateRecordingDurationFromChunks } from "./duration-calculation.util";
import { useAmbientScribeCaptureVisit } from "./hooks/use-ambient-scribe-capture-visit.hook";
import { createLogger } from "./logging.util";
import {
  AUDIO_CONFIG,
  ERROR_MESSAGES,
  FAVICON_PATHS,
  MEDIA_RECORDER_STATES,
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED,
  SUCCESS_MESSAGES,
  TAB_SYNC_ACTIONS,
  UPLOAD_MESSAGES
} from "./medical-scribe-constants";
import type {
  MedicalScribeContextType,
  MedicalScribeThinContextType
} from "./medical-scribe-context-type.type";
import type { RecordingData } from "./medical-scribe-db/index-db-types.type";
import MedicalScribeRecordingWidget from "./medical-scribe-recording-widget";
import {
  pauseScribeRecording,
  resumeScribeRecording,
  savingInProgressScribeRecording,
  selectMedicalScribeDeviceSettings,
  startScribeRecording,
  stopScribeRecording,
  updateScribeVolume
} from "./medical-scribe.slice";
import type { MicrophoneInput } from "./microphone.utils";
import {
  clearRecordingSession,
  getRecordingSession,
  restoreRecordingSession,
  saveRecordingSession,
  updateRecordingSessionState
} from "./recording-session.util";
import AudioWorker from "./save-audio-worker/save-audio-worker?worker";
import type {
  UploadWorkerInterface,
  UploadWorkerType
} from "./save-audio-worker/upload-audio-worker";
import UploadWorker from "./save-audio-worker/upload-audio-worker?worker";
import { MedicalScribeStreamingContext } from "./use-medical-scribe-streaming.hook";
import {
  CHUNK_TIMESLICE_MS,
  MAX_RECORDING_DURATION_MS,
  formatDurationSeconds,
  recordToDataUrl
} from "./utils";

type UploadWorkerInstance = Comlink.Remote<UploadWorkerInterface>;

export const MedicalScribeContext =
  createContext<MedicalScribeContextType | null>(null);

type MedicalScribeProviderProps = {
  children?: ReactNode;
};

export const MedicalScribeProvider = ({
  children
}: MedicalScribeProviderProps) => {
  const { isSunoAdminOrBusinessOwner, isAudiologist } = useUserRoles();
  const flags = useFlags();
  const isMedicalScribeEnabled =
    flags[MEDICAL_SCRIBE_FEATURE] &&
    (isSunoAdminOrBusinessOwner || isAudiologist);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);

  const deviceSettings = useAppSelector(selectMedicalScribeDeviceSettings);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioWorkerRef = useRef<Comlink.Remote<any> | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);

  const audioWorkerInstanceRef = useRef<Worker | null>(null);
  const uploadWorkerRef = useRef<UploadWorkerInstance | null>(null);
  const uploadWorkerInstanceRef = useRef<Worker | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [, setCurrentPath] = useState(location.pathname);
  const isStoppingRef = useRef<boolean>(false);
  // Track all final chunk promises to ensure they're persisted before saving
  // Both requestData() and stop() can fire final chunks, so we need to track all of them
  const finalChunkPromisesRef = useRef<Promise<void>[]>([]);

  /**
   * Wait for all chunks to be received and saved to IndexedDB.
   * Uses polling to detect when chunks stabilize (no new chunks arriving).
   */
  const waitForAllChunksToComplete = useCallback(async (): Promise<void> => {
    const waitStartTime = Date.now();
    const initialChunkCount = finalChunkPromisesRef.current.length;
    createLogger.log(
      `[waitForAllChunksToComplete] Starting - initial chunk count: ${initialChunkCount}`,
      recordingStartTimeRef.current
    );

    const MAX_POLL_ATTEMPTS = 150; // Maximum 15 seconds (150 * 100ms) - very generous for edge cases
    const POLL_INTERVAL_MS = 100;
    const STABLE_THRESHOLD = 10; // Need 10 consecutive polls (1 second) with no new chunks - very conservative

    let lastChunkCount = finalChunkPromisesRef.current.length;
    let stableCount = 0;
    let attempts = 0;

    // Phase 1: Wait for chunks to stabilize (no new chunks arriving)
    while (attempts < MAX_POLL_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      const currentChunkCount = finalChunkPromisesRef.current.length;

      if (currentChunkCount > lastChunkCount) {
        // New chunks arrived, reset stability counter
        const waitElapsed = ((Date.now() - waitStartTime) / 1000).toFixed(3);
        createLogger.log(
          `[waitForAllChunksToComplete] New chunks detected: ${currentChunkCount} (was ${lastChunkCount}), resetting stability counter (attempt ${attempts}, wait elapsed: ${waitElapsed}s)`,
          recordingStartTimeRef.current
        );
        lastChunkCount = currentChunkCount;
        stableCount = 0;
      } else if (
        currentChunkCount === lastChunkCount &&
        currentChunkCount > 0
      ) {
        // Chunk count is stable
        stableCount++;
        if (stableCount >= STABLE_THRESHOLD) {
          const waitElapsed = ((Date.now() - waitStartTime) / 1000).toFixed(3);
          createLogger.log(
            `[waitForAllChunksToComplete] Chunks stabilized at ${currentChunkCount} after ${stableCount} stable polls (${attempts} total attempts, wait elapsed: ${waitElapsed}s)`,
            recordingStartTimeRef.current
          );
          break;
        }
      } else {
        lastChunkCount = currentChunkCount;
        if (currentChunkCount > 0) {
          stableCount++;
        }
      }

      attempts++;
    }

    if (attempts >= MAX_POLL_ATTEMPTS) {
      const waitElapsed = ((Date.now() - waitStartTime) / 1000).toFixed(3);
      createLogger.warn(
        `[waitForAllChunksToComplete] Max poll attempts reached (${attempts}), proceeding with ${lastChunkCount} chunks (started with ${initialChunkCount}, wait elapsed: ${waitElapsed}s)`,
        recordingStartTimeRef.current
      );
    }

    const finalChunkCount = finalChunkPromisesRef.current.length;
    const waitElapsed = ((Date.now() - waitStartTime) / 1000).toFixed(3);
    createLogger.log(
      `[waitForAllChunksToComplete] Phase 1 complete - final chunk count: ${finalChunkCount} (started with ${initialChunkCount}, gained ${finalChunkCount - initialChunkCount}, wait elapsed: ${waitElapsed}s)`,
      recordingStartTimeRef.current
    );

    // Phase 2: Wait for all tracked chunks to complete (save to IndexedDB)
    const phase2StartTime = Date.now();
    if (finalChunkPromisesRef.current.length > 0) {
      createLogger.log(
        `[waitForAllChunksToComplete] Phase 2: Waiting for ${finalChunkPromisesRef.current.length} chunk promises to complete...`,
        recordingStartTimeRef.current
      );
      try {
        await Promise.all(finalChunkPromisesRef.current);
        const phase2Elapsed = ((Date.now() - phase2StartTime) / 1000).toFixed(
          3
        );
        createLogger.log(
          `[waitForAllChunksToComplete] Phase 2 complete - All ${finalChunkPromisesRef.current.length} chunk promises completed (phase 2 elapsed: ${phase2Elapsed}s)`,
          recordingStartTimeRef.current
        );
      } catch (error) {
        createLogger.error(
          "[waitForAllChunksToComplete] Error waiting for chunks:",
          error,
          recordingStartTimeRef.current
        );
      }
    } else {
      createLogger.warn(
        "[waitForAllChunksToComplete] No chunks to wait for - this might indicate a problem",
        recordingStartTimeRef.current
      );
    }

    // Phase 3: Wait for worker's pending operations to complete
    // This ensures ALL IndexedDB writes are complete
    const phase3StartTime = Date.now();
    if (audioWorkerRef.current) {
      createLogger.log(
        "[waitForAllChunksToComplete] Phase 3: Waiting for worker's pending operations...",
        recordingStartTimeRef.current
      );
      try {
        await audioWorkerRef.current.waitForAllChunks();
        const phase3Elapsed = ((Date.now() - phase3StartTime) / 1000).toFixed(
          3
        );
        createLogger.log(
          `[waitForAllChunksToComplete] Phase 3 complete - Worker's pending operations completed (phase 3 elapsed: ${phase3Elapsed}s)`,
          recordingStartTimeRef.current
        );
      } catch (error) {
        createLogger.error(
          "[waitForAllChunksToComplete] Error waiting for worker operations:",
          error,
          recordingStartTimeRef.current
        );
      }
    }

    const totalWaitElapsed = ((Date.now() - waitStartTime) / 1000).toFixed(3);
    createLogger.log(
      `[waitForAllChunksToComplete] All phases complete! Total chunks processed: ${finalChunkCount} (total wait elapsed: ${totalWaitElapsed}s)`,
      recordingStartTimeRef.current
    );
  }, []);

  const editorRef = useRef<Editor | null | undefined>(undefined);
  const insertScribeSelectionBookmarkRef = useRef<Bookmark | null>(null);
  const setEditorRef = useCallback((editor: Editor | undefined | null) => {
    editorRef.current = editor;
  }, []);

  useEventBus(LAST_FOCUSED_WORD_PROCESSOR_FIELD, (editor) => {
    editorRef.current = editor;
  });

  useEffect(() => {
    if (location.pathname) {
      setEditorRef(null);
    }
  }, [location, setEditorRef]);

  // Add a ref to track if database is ready
  const isDatabaseReadyRef = useRef<boolean>(false);

  // Track if there's an active recording on another tab
  const [isRecordingActiveOnAnotherTab, setIsRecordingActiveOnAnotherTab] =
    useState(false);
  const processedMessageIds = useRef(new Set<string>());

  const [createAmbientScribe] = useCreateAmbientScribeMutation();
  const [generateSoapNotes] = useGenerateAmbientSoapNotesMutation();

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // Handle cross-tab messages
  const handleTabSyncMessage = useCallback(
    async (message: SyncMessage & { action: string; payload?: any }) => {
      const { emitterId, action } = message;

      // Ignore messages from this tab
      if (emitterId === appInstanceId) return;

      createLogger.log(
        `Received medical scribe tab sync message: action=${action}`,
        null
      );

      switch (action) {
        case TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB:
        case TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB:
          setIsRecordingActiveOnAnotherTab(true);
          break;
        case TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB:
          // Keep widget hidden for paused state too
          setIsRecordingActiveOnAnotherTab(true);
          break;
        case TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB:
          setIsRecordingActiveOnAnotherTab(false);
          break;
        case TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS: {
          // Another tab is asking if we have an active recording
          // Respond if we do
          const session = await getRecordingSession();
          if (
            session &&
            session.tabInstanceId === appInstanceId &&
            (session.recordingState === RECORDING_STATE_IN_PROGRESS ||
              session.recordingState === RECORDING_STATE_PAUSED)
          ) {
            createLogger.log(
              "Responding to recording status request - this tab owns an active recording",
              null
            );
            dispatchTabSyncMessage({
              action: TAB_SYNC_ACTIONS.RECORDING_STATUS_RESPONSE,
              payload: { ownerTabId: appInstanceId }
            });
          }
          break;
        }
        default:
          break;
      }
    },
    []
  );

  // Listen for messages from other tabs
  useSyncBetweenTabs<SyncMessage & { action: string; payload?: any }>(
    processedMessageIds,
    handleTabSyncMessage,
    !isMedicalScribeEnabled
  );

  // Check IndexedDB for existing recording session on mount
  useEffect(() => {
    if (!isMedicalScribeEnabled) return;

    let timeoutId: NodeJS.Timeout | null = null;
    let cleanupHandler: ((event: MessageEvent) => void) | null = null;
    let isMounted = true;

    const checkForActiveRecording = async () => {
      try {
        const session = await getRecordingSession();
        if (
          session &&
          (session.recordingState === RECORDING_STATE_IN_PROGRESS ||
            session.recordingState === RECORDING_STATE_PAUSED)
        ) {
          createLogger.log(
            `Found active recording session in IndexedDB on mount: tabInstanceId=${session.tabInstanceId}, currentTabId=${appInstanceId}`,
            null
          );

          // Check if the recording belongs to this tab or another tab
          if (
            session.tabInstanceId &&
            session.tabInstanceId !== appInstanceId
          ) {
            // Recording might belong to another tab - check if that tab is still alive
            createLogger.log(
              `Found session belonging to another tab (ID: ${session.tabInstanceId}), checking if that tab is still active...`,
              null
            );

            let ownerTabIsAlive = false;

            const handleStatusResponse = (event: MessageEvent) => {
              try {
                const data = JSON.parse(event.data);
                if (
                  data.action === TAB_SYNC_ACTIONS.RECORDING_STATUS_RESPONSE &&
                  data.emitterId === session.tabInstanceId
                ) {
                  ownerTabIsAlive = true;
                  createLogger.log(
                    "Owner tab is still alive, hiding widget on this tab",
                    null
                  );
                  if (isMounted) {
                    setIsRecordingActiveOnAnotherTab(true);
                  }
                }
              } catch {}
            };

            // Store handler reference for cleanup
            cleanupHandler = handleStatusResponse;
            syncChannel.addEventListener("message", handleStatusResponse);

            // Request status from the owner tab
            dispatchTabSyncMessage({
              action: TAB_SYNC_ACTIONS.REQUEST_RECORDING_STATUS,
              payload: { requestingTabId: appInstanceId }
            });

            // Wait 2 seconds for response
            timeoutId = setTimeout(async () => {
              syncChannel.removeEventListener("message", handleStatusResponse);
              cleanupHandler = null;

              // Check if component is still mounted before proceeding
              if (!isMounted) {
                createLogger.log(
                  "Component unmounted, skipping orphaned session handling",
                  null
                );
                return;
              }

              if (!ownerTabIsAlive) {
                // No response - the owner tab is closed, this is an orphaned session
                createLogger.log(
                  "No response from owner tab - session is orphaned. This tab will claim ownership.",
                  null
                );

                // Wait for workers to be initialized before restoring
                // Workers should be ready by now (2 seconds after mount), but check to be safe
                if (audioWorkerRef.current) {
                  await restoreRecordingSession(
                    session,
                    dispatch,
                    audioWorkerRef,
                    currentUser
                  );

                  // Clear the flag to show the widget (only if still mounted)
                  if (isMounted) {
                    setIsRecordingActiveOnAnotherTab(false);
                  }
                } else {
                  createLogger.log(
                    "Workers not ready yet, will restore when workers initialize",
                    null
                  );
                  // Update the session so when workers do initialize, they'll restore it
                  await saveRecordingSession({
                    ...session,
                    tabInstanceId: appInstanceId
                  });
                }
              }
            }, 2000);
          } else {
            // Recording belongs to this tab (after reload) or has no tabInstanceId (old session)
            // The worker initialization effect will handle restoration
            createLogger.log(
              "Recording belongs to this tab or is legacy session without tabInstanceId",
              null
            );
          }
        }
      } catch (error) {
        createLogger.error(
          "Failed to check for active recording on mount:",
          error,
          null
        );
      }
    };

    checkForActiveRecording();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;

      // Clear timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Remove event listener if it exists
      if (cleanupHandler) {
        syncChannel.removeEventListener("message", cleanupHandler);
      }
    };
    // dispatch and currentUser are stable, only run once on mount when feature is enabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMedicalScribeEnabled]);

  // Periodic heartbeat to broadcast current recording state
  // This ensures tabs stay synchronized even if a message is missed
  useEffect(() => {
    if (!isMedicalScribeEnabled) return;

    const heartbeatInterval = setInterval(() => {
      // Only broadcast if we have an active recording on this tab
      if (mediaRecorderRef.current) {
        const state = mediaRecorderRef.current.state;

        if (state === MEDIA_RECORDER_STATES.RECORDING) {
          dispatchTabSyncMessage({
            action: TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB,
            emitterId: appInstanceId
          });
        } else if (state === MEDIA_RECORDER_STATES.PAUSED) {
          dispatchTabSyncMessage({
            action: TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
            emitterId: appInstanceId
          });
        }
      }
    }, 5000); // Broadcast every 5 seconds

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [isMedicalScribeEnabled]);

  const uploadFinished = useCallback(
    (
      patientFullName: string | undefined,
      patientId: number,
      isError: boolean
    ) => {
      setIsUploading(false);
      // Use a ref to get the latest snackbar state without causing re-renders
      const currentSnackbarState = selectSnackbarOpen(store.getState());
      if (!currentSnackbarState) {
        if (isError) {
          dispatch(
            setCustomMessage(
              `${SUCCESS_MESSAGES.SCRIBE_RECORD_UPLOAD_FAILED} ${
                patientFullName ? "for " + patientFullName : ""
              }`
            )
          );
        } else {
          dispatch(
            setCustomMessage(
              `The scribe record ${
                patientFullName ? "for " + patientFullName : ""
              } uploaded and${NON_BREAKABLE_SPACE}transcribed. SOAP${NON_BREAKABLE_SPACE}Notes are${NON_BREAKABLE_SPACE}ready and now can${NON_BREAKABLE_SPACE}be${NON_BREAKABLE_SPACE}inserted into the Chart Note.`
            )
          );
        }

        dispatch(setOpen(10000));
      }
    },
    [dispatch]
  );

  const uploadAmbientScribeFn = useCallback(
    async (recording: RecordingData, blob: Blob) => {
      setIsUploading(true);

      const file = new File(
        [blob],
        `${recording.id}${AUDIO_CONFIG.FILE_EXTENSION}`,
        {
          type: blob.type
        }
      );

      // Get current practice from store to avoid dependency changes
      const currentState = store.getState();
      const currentPracticeId = selectCurrentPractice(currentState)?.id;

      const response = await createAmbientScribe({
        patient: recording.patientId,
        duration: formatDurationSeconds(recording.duration || 0),
        staff: Number(recording.userId),
        practice: currentPracticeId,
        audio_file: file,
        clinic: currentClinic?.id
      });

      if ("error" in response) {
        createLogger.error("Upload failed with error:", response.error, null);
        return null;
      }

      const ambientScribeId = Number(response.data?.id);
      if (!ambientScribeId) {
        createLogger.error("Upload succeeded but no ID returned", null, null);
        return null;
      }

      // Generate SOAP Notes after successful upload
      const soapNotesResponse = await generateSoapNotes({
        id: ambientScribeId
      });

      if ("error" in soapNotesResponse) {
        createLogger.error(
          "Scribe generation failed with error:",
          soapNotesResponse.error,
          null
        );
        return null;
      }

      return {
        id: Number(ambientScribeId),
        created_at: response.data?.created_at
      };
    },
    [createAmbientScribe, generateSoapNotes, currentClinic?.id]
  );

  const { startAnalysis, stopAnalysis } = useAudioAnalyzer({
    onVolumeChange: (volume, isMicActive) =>
      dispatch(updateScribeVolume({ volume, isMicActive }))
  });

  const audioRecordingCleanup = useCallback(() => {
    // Reset recording start time
    recordingStartTimeRef.current = null;

    // Stop analyser RAF loop and close AudioContext (owned by useAudioAnalyzer)
    stopAnalysis();

    // Stop and cleanup media tracks first (before closing AudioContext)
    // This ensures media stream is properly released
    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          track.enabled = false; // Additional safeguard
        });
      } catch (error) {
        createLogger.warn("Error stopping media stream tracks:", null);
      }
    }

    // Note: We don't stop MediaRecorder here because:
    // 1. If called from onstop handler, recorder is already stopped
    // 2. If called from other places (component unmount), we only need to stop tracks
    // The recorder will be stopped by its onstop handler or explicitly via stopRecording()

    // Clear all refs after cleanup
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;

    // If recorder still exists and is active, log a warning (shouldn't happen)
    if (recorder && recorder.state !== MEDIA_RECORDER_STATES.INACTIVE) {
      createLogger.warn(
        "MediaRecorder was still active during cleanup, this shouldn't happen",
        null
      );
    }
  }, [stopAnalysis]);

  const isReady = Boolean(currentUser?.id && currentPractice?.id);

  const workersCleanup = useCallback(async () => {
    try {
      // Clean up upload worker
      const uploadWorker = uploadWorkerRef.current;
      if (uploadWorker) {
        createLogger.log("Cleaning up upload worker...", null);
        await uploadWorker.cleanup();
        uploadWorker[Comlink.releaseProxy]();
      }
    } catch (e) {
      createLogger.error("Upload worker cleanup error:", e, null);
    }

    // Clean up audio worker
    if (audioWorkerInstanceRef.current) {
      try {
        createLogger.log("Terminating audio worker...", null);
        audioWorkerInstanceRef.current.terminate();
      } catch (e) {
        createLogger.error("Audio worker cleanup error:", e, null);
      }
    }

    // Clean up upload worker instance
    if (uploadWorkerInstanceRef.current) {
      try {
        createLogger.log("Terminating upload worker instance...", null);
        uploadWorkerInstanceRef.current.terminate();
      } catch (e) {
        createLogger.error("Upload worker instance cleanup error:", e, null);
      }
    }

    // Reset all refs
    audioWorkerInstanceRef.current = null;
    audioWorkerRef.current = null;
    uploadWorkerRef.current = null;
    uploadWorkerInstanceRef.current = null;
    isDatabaseReadyRef.current = false;

    // Note: We don't clear the recording session here because this cleanup
    // might be triggered by navigation or errors, not intentional recording stop
  }, []);

  useEffect(() => {
    if (!isMedicalScribeEnabled || !isReady) return;

    let isCancelled = false;

    const initializeWorkers = async () => {
      try {
        // First, verify database integrity in main thread
        const isDbValid = await verifyDatabaseIntegrity();
        if (!isDbValid) {
          createLogger.error(
            "Database integrity check failed, cannot initialize workers",
            undefined,
            null
          );
          return;
        }

        isDatabaseReadyRef.current = true;

        // Allow UI to update before heavy worker initialization
        queueMicrotask(async () => {
          const audioWorkerInstance = new AudioWorker();
          const uploadWorkerInstance = new UploadWorker();

          // Store instances for cleanup
          audioWorkerInstanceRef.current = audioWorkerInstance;
          uploadWorkerInstanceRef.current = uploadWorkerInstance;

          audioWorkerRef.current = Comlink.wrap(audioWorkerInstance);
          const UploadWorkerClass =
            Comlink.wrap<UploadWorkerType>(uploadWorkerInstance);

          const uploadWorkerProxy = (await new UploadWorkerClass(
            Comlink.proxy(uploadAmbientScribeFn),
            Comlink.proxy(uploadFinished)
          )) as unknown as UploadWorkerInstance;

          if (isCancelled) {
            createLogger.log("Worker initialization cancelled", null);
            audioWorkerInstance.terminate();
            uploadWorkerInstance.terminate();
            return;
          }

          uploadWorkerRef.current = uploadWorkerProxy;
          createLogger.log("Workers initialized successfully", null);

          try {
            await uploadWorkerProxy.processQueue();
          } catch (error) {
            createLogger.error(
              "Upload worker connection test failed:",
              error,
              null
            );
          }

          // Check for persisted recording session after workers are initialized
          try {
            const persistedSession = await getRecordingSession();
            if (persistedSession) {
              createLogger.log(
                `Found persisted recording session: recordingId=${persistedSession.recordingId}, tabInstanceId=${persistedSession.tabInstanceId}, currentTabId=${appInstanceId}`,
                null
              );

              // Only restore if the session was actually recording (not stopped)
              if (
                persistedSession.recordingState ===
                  RECORDING_STATE_IN_PROGRESS ||
                persistedSession.recordingState === RECORDING_STATE_PAUSED
              ) {
                // Check if this session belongs to this tab
                // If tabInstanceId matches or doesn't exist (legacy session), restore it
                // If tabInstanceId doesn't match, it belongs to another tab
                if (
                  !persistedSession.tabInstanceId ||
                  persistedSession.tabInstanceId === appInstanceId
                ) {
                  createLogger.log(
                    "Session belongs to this tab, restoring...",
                    null
                  );
                  // Restore the complete recording session infrastructure
                  await restoreRecordingSession(
                    persistedSession,
                    dispatch,
                    audioWorkerRef,
                    currentUser
                  );
                } else {
                  createLogger.log(
                    `Session belongs to another tab (ID: ${persistedSession.tabInstanceId}), not restoring on this tab`,
                    null
                  );
                  // The session belongs to another tab, so hide widget on this tab
                  setIsRecordingActiveOnAnotherTab(true);
                }
              } else {
                // Clean up stale stopped sessions
                await clearRecordingSession();
              }
            }
          } catch (error) {
            createLogger.error(
              "Failed to check for persisted recording session:",
              error,
              null
            );
          }
        });
      } catch (error) {
        createLogger.error("Failed to initialize workers:", error, null);
      }
    };

    initializeWorkers();

    return () => {
      isCancelled = true;

      // Cleanup audio recording resources
      // This is safe even if recording is active - cleanup handles it gracefully
      audioRecordingCleanup();

      // Cleanup workers asynchronously to avoid blocking unmount
      // Use void to explicitly ignore promise (cleanup should not block unmount)
      void workersCleanup();
    };
  }, [
    isMedicalScribeEnabled,
    isReady,
    audioRecordingCleanup,
    uploadAmbientScribeFn,
    workersCleanup,
    uploadFinished,
    dispatch,
    currentUser
  ]);

  const stopRecording = useCallback(async () => {
    const stopStartTime = Date.now();
    const recordingDuration = createLogger.getRecordingDuration(
      recordingStartTimeRef.current
    );
    createLogger.log(
      `[stopRecording] Starting stop sequence - Recording duration: ${recordingDuration}`,
      recordingStartTimeRef.current
    );

    // Race condition protection: Prevent double-click on Stop button
    if (isStoppingRef.current) {
      createLogger.warn(
        "[stopRecording] Stop recording already in progress, ignoring duplicate request",
        recordingStartTimeRef.current
      );
      return;
    }

    // Set flag immediately to prevent concurrent stop attempts
    // This must happen before any async operations
    isStoppingRef.current = true;

    try {
      // Update Redux state immediately for responsive UI
      // Show saving state immediately when user confirms stop
      createLogger.log(
        "[stopRecording] Dispatching savingInProgressScribeRecording...",
        recordingStartTimeRef.current
      );
      dispatch(savingInProgressScribeRecording());
      const dispatchElapsed = ((Date.now() - stopStartTime) / 1000).toFixed(3);
      createLogger.log(
        `[stopRecording] savingInProgressScribeRecording dispatched (elapsed: ${dispatchElapsed}s)`,
        recordingStartTimeRef.current
      );

      // Force a microtask to ensure React has processed the Redux state update
      await new Promise((resolve) => setTimeout(resolve, 0));

      setFavicon(FAVICON_PATHS.DEFAULT);

      // If we have an active MediaRecorder, stop it normally
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== MEDIA_RECORDER_STATES.INACTIVE) {
        // Don't clear finalChunkPromisesRef here - we want to track ALL chunks
        // that arrive during the stop sequence, including any that might have already
        // been added before stopRecording() was called
        // Instead, we'll track the count before stop() to see how many new ones arrive
        const initialFinalChunkCount = finalChunkPromisesRef.current.length;
        const stopElapsed = ((Date.now() - stopStartTime) / 1000).toFixed(3);
        createLogger.log(
          `[stopRecording] Starting stop sequence with ${initialFinalChunkCount} already-tracked chunks (elapsed: ${stopElapsed}s, recording duration: ${recordingDuration})`,
          recordingStartTimeRef.current
        );

        // Flush ALL buffered audio data before stopping
        // MediaRecorder buffers up to CHUNK_TIMESLICE_MS (3 seconds) of audio
        // We need multiple requestData() calls to ensure we get everything
        if (
          recorder.state === MEDIA_RECORDER_STATES.RECORDING ||
          recorder.state === MEDIA_RECORDER_STATES.PAUSED
        ) {
          // First flush: Get any buffered data from current state
          const flush1StartTime = Date.now();
          const flush1Elapsed = (
            (flush1StartTime - stopStartTime) /
            1000
          ).toFixed(3);
          createLogger.log(
            `[stopRecording] Flush #1: Calling requestData() - recorder state: ${recorder.state}, ` +
              `finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
              `elapsed: ${flush1Elapsed}s, recording duration: ${recordingDuration}`,
            recordingStartTimeRef.current
          );
          recorder.requestData();
          await new Promise((resolve) => setTimeout(resolve, 500));
          const flush1AfterElapsed = (
            (Date.now() - flush1StartTime) /
            1000
          ).toFixed(3);
          createLogger.log(
            `[stopRecording] Flush #1 completed - finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
              `flush took: ${flush1AfterElapsed}s`,
            recordingStartTimeRef.current
          );

          // Second flush: If paused, we might have accumulated more data
          // If recording, this gets any data that arrived after first flush
          const currentState = recorder.state;
          if (
            currentState === MEDIA_RECORDER_STATES.RECORDING ||
            currentState === MEDIA_RECORDER_STATES.PAUSED
          ) {
            const flush2StartTime = Date.now();
            const flush2Elapsed = (
              (flush2StartTime - stopStartTime) /
              1000
            ).toFixed(3);
            createLogger.log(
              `[stopRecording] Flush #2: Calling requestData() again - recorder state: ${currentState}, ` +
                `finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
                `elapsed: ${flush2Elapsed}s, recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );
            recorder.requestData();
            await new Promise((resolve) => setTimeout(resolve, 500));
            const flush2AfterElapsed = (
              (Date.now() - flush2StartTime) /
              1000
            ).toFixed(3);
            createLogger.log(
              `[stopRecording] Flush #2 completed - finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
                `flush took: ${flush2AfterElapsed}s`,
              recordingStartTimeRef.current
            );
          }

          const afterFlushesElapsed = (
            (Date.now() - stopStartTime) /
            1000
          ).toFixed(3);
          createLogger.log(
            `[stopRecording] After flushes - finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
              `total elapsed: ${afterFlushesElapsed}s, recording duration: ${recordingDuration}`,
            recordingStartTimeRef.current
          );
        }

        // Stop the recorder - this will fire a final ondataavailable event with remaining buffered data
        const beforeStopElapsed = ((Date.now() - stopStartTime) / 1000).toFixed(
          3
        );
        createLogger.log(
          `[stopRecording] Calling stop() - recorder state: ${recorder.state}, ` +
            `finalChunksTracked before: ${finalChunkPromisesRef.current.length}, ` +
            `elapsed: ${beforeStopElapsed}s, recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        // Track chunk count BEFORE stop() so we can detect new chunks
        // Note: We don't clear finalChunkPromisesRef anymore - we track ALL chunks
        // that arrive during the stop sequence to ensure nothing is lost
        const chunksBeforeStop = finalChunkPromisesRef.current.length;
        createLogger.log(
          `[stopRecording] Chunks tracked before stop(): ${chunksBeforeStop}, recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        const stopCallTime = Date.now();
        recorder.stop();
        const stopCallElapsed = ((Date.now() - stopCallTime) / 1000).toFixed(3);
        const totalElapsedAfterStop = (
          (Date.now() - stopStartTime) /
          1000
        ).toFixed(3);
        createLogger.log(
          `[stopRecording] stop() called, recorder state now: ${recorder.state}, ` +
            `stop() call took: ${stopCallElapsed}s, total elapsed: ${totalElapsedAfterStop}s, ` +
            `recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        // Poll for new chunks from stop() - don't use fixed timeout
        // MediaRecorder may take variable time to flush all buffered data
        // We need to wait until no new chunks arrive for a period
        const pollStartTime = Date.now();
        let stopChunkPollAttempts = 0;
        const MAX_STOP_CHUNK_POLLS = 30; // 3 seconds max (30 * 100ms)
        let lastStopChunkCount = finalChunkPromisesRef.current.length;
        let stopChunkStableCount = 0;
        const STOP_CHUNK_STABLE_THRESHOLD = 5; // 500ms of stability

        createLogger.log(
          `[stopRecording] Starting polling for stop() chunks - initial count: ${lastStopChunkCount}, ` +
            `recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        while (stopChunkPollAttempts < MAX_STOP_CHUNK_POLLS) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          stopChunkPollAttempts++;

          const currentStopChunkCount = finalChunkPromisesRef.current.length;
          const pollElapsed = ((Date.now() - pollStartTime) / 1000).toFixed(3);
          const totalElapsedPolling = (
            (Date.now() - stopStartTime) /
            1000
          ).toFixed(3);

          if (currentStopChunkCount > lastStopChunkCount) {
            createLogger.log(
              `[stopRecording] New chunk from stop() detected: ${currentStopChunkCount} (was ${lastStopChunkCount}), ` +
                `continuing to poll... (poll elapsed: ${pollElapsed}s, total: ${totalElapsedPolling}s, ` +
                `recording duration: ${recordingDuration})`,
              recordingStartTimeRef.current
            );
            lastStopChunkCount = currentStopChunkCount;
            stopChunkStableCount = 0;
          } else if (
            currentStopChunkCount === lastStopChunkCount &&
            currentStopChunkCount > chunksBeforeStop
          ) {
            // Chunk count is stable and we got at least one new chunk from stop()
            stopChunkStableCount++;
            if (stopChunkStableCount >= STOP_CHUNK_STABLE_THRESHOLD) {
              createLogger.log(
                `[stopRecording] Stop() chunks stabilized at ${currentStopChunkCount} after ${stopChunkStableCount} stable polls ` +
                  `(poll elapsed: ${pollElapsed}s, total: ${totalElapsedPolling}s, recording duration: ${recordingDuration})`,
                recordingStartTimeRef.current
              );
              break;
            }
          } else {
            lastStopChunkCount = currentStopChunkCount;
            stopChunkStableCount++;
          }
        }

        const pollTotalElapsed = ((Date.now() - pollStartTime) / 1000).toFixed(
          3
        );
        const totalElapsedAfterPolling = (
          (Date.now() - stopStartTime) /
          1000
        ).toFixed(3);
        const newChunksFromStop =
          finalChunkPromisesRef.current.length - chunksBeforeStop;
        createLogger.log(
          `[stopRecording] After stop() polling - finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
            `new chunks from stop(): ${newChunksFromStop}, ` +
            `poll elapsed: ${pollTotalElapsed}s, total elapsed: ${totalElapsedAfterPolling}s, ` +
            `recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        // EXTRA SAFETY: If recorder might still have buffered data, try one more requestData()
        // This is a last-ditch effort to capture any remaining audio
        // Note: This won't work if recorder is already INACTIVE, but it's worth trying
        const recorderStateAfterStop = recorder.state;
        if (
          recorderStateAfterStop === MEDIA_RECORDER_STATES.RECORDING ||
          recorderStateAfterStop === MEDIA_RECORDER_STATES.PAUSED
        ) {
          const finalRequestStartTime = Date.now();
          const finalRequestElapsed = (
            (finalRequestStartTime - stopStartTime) /
            1000
          ).toFixed(3);
          createLogger.log(
            `[stopRecording] Recorder still not INACTIVE (${recorderStateAfterStop}), ` +
              `attempting final requestData()... (elapsed: ${finalRequestElapsed}s, ` +
              `recording duration: ${recordingDuration})`,
            recordingStartTimeRef.current
          );
          try {
            recorder.requestData();
            await new Promise((resolve) => setTimeout(resolve, 300));
            const finalRequestAfterElapsed = (
              (Date.now() - finalRequestStartTime) /
              1000
            ).toFixed(3);
            const totalElapsedAfterFinalRequest = (
              (Date.now() - stopStartTime) /
              1000
            ).toFixed(3);
            createLogger.log(
              `[stopRecording] After final requestData() - finalChunksTracked: ${finalChunkPromisesRef.current.length}, ` +
                `final request took: ${finalRequestAfterElapsed}s, total elapsed: ${totalElapsedAfterFinalRequest}s, ` +
                `recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );
          } catch (error) {
            createLogger.warn(
              `[stopRecording] Final requestData() failed (expected if recorder is stopping):`,
              recordingStartTimeRef.current
            );
          }
        }

        const totalStopElapsed = ((Date.now() - stopStartTime) / 1000).toFixed(
          3
        );
        createLogger.log(
          `[stopRecording] Stop sequence complete - total elapsed: ${totalStopElapsed}s, ` +
            `final chunks tracked: ${finalChunkPromisesRef.current.length}, ` +
            `recording duration: ${recordingDuration}`,
          recordingStartTimeRef.current
        );

        // Broadcast to other tabs that recording stopped on this tab
        dispatchTabSyncMessage({
          action: TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB,
          emitterId: appInstanceId
        });

        // Note: isStoppingRef will be reset in onstop handler
        // Don't reset it here to prevent race conditions
        return;
      }

      // If no active MediaRecorder (e.g., restored session), handle stop differently
      const persistedSession = await getRecordingSession();
      if (persistedSession) {
        createLogger.log(
          `Stopping restored session: recordingId=${persistedSession.recordingId}`,
          null
        );

        // Clear the persisted session
        await clearRecordingSession();

        // Broadcast to other tabs that recording stopped on this tab
        dispatchTabSyncMessage({
          action: TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB,
          emitterId: appInstanceId
        });

        setFavicon(FAVICON_PATHS.DEFAULT);
        dispatch(stopScribeRecording(""));

        // Reset stopping flag since we're done (no onstop handler in this path)
        isStoppingRef.current = false;

        createLogger.log("Restored session stopped and cleared", null);
      } else {
        createLogger.warn(ERROR_MESSAGES.NO_RECORDING_SESSION_TO_STOP, null);
        setFavicon(FAVICON_PATHS.DEFAULT);
        // Reset flag if no session found
        isStoppingRef.current = false;
      }
    } catch (error) {
      createLogger.error("Failed to stop recording:", error, null);
      // Reset flag on error to allow retry
      isStoppingRef.current = false;
      dispatch(stopScribeRecording(""));
      setFavicon(FAVICON_PATHS.DEFAULT);
    }
  }, [dispatch]);

  // Helper function for starting recording with existing ID (for resume scenarios)
  const startRecordingWithExistingId = useCallback(
    async (params: {
      patientId: number | undefined;
      patientFullName: string | undefined;
      chartNoteId: number | undefined;
      existingRecordingId: string;
    }) => {
      // Check if database is ready before starting recording
      if (!isDatabaseReadyRef.current) {
        createLogger.error(ERROR_MESSAGES.DATABASE_NOT_READY, undefined, null);
        return;
      }

      const { patientId, patientFullName, existingRecordingId } = params;
      const userId = currentUser?.id;
      const deviceId = deviceSettings.microphone;

      try {
        createLogger.log(
          `Resuming recording with existing ID: ${existingRecordingId}`,
          recordingStartTimeRef.current
        );

        const constraints: MediaStreamConstraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        mediaStreamRef.current = stream;
        const suggestedBitrate = await detectSuggestedBitrate(stream);

        await startAnalysis(stream);

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: AUDIO_CONFIG.MIME_TYPE,
          audioBitsPerSecond: suggestedBitrate
        });
        mediaRecorderRef.current = mediaRecorder;

        // Clear any previous final chunk promises from previous recording
        finalChunkPromisesRef.current = [];

        // Resume existing recording with the audio worker
        await audioWorkerRef.current?.startRecording({
          patientId,
          userId,
          patientFullName,
          existingRecordingId
        });

        mediaRecorder.ondataavailable = async (event) => {
          const chunkReceivedTime = Date.now();
          const recorderState = mediaRecorderRef.current?.state || "unknown";
          const chunkSize = event.data?.size || 0;
          const isStopping = isStoppingRef.current;
          const recordingDuration = createLogger.getRecordingDuration(
            recordingStartTimeRef.current
          );

          createLogger.log(
            `[ondataavailable RESUME] Chunk received: size=${chunkSize} bytes, ` +
              `recorderState=${recorderState}, isStopping=${isStopping}, ` +
              `finalChunksTracked=${finalChunkPromisesRef.current.length}, ` +
              `recording duration: ${recordingDuration}`,
            recordingStartTimeRef.current
          );

          //Process ALL chunks with data, regardless of recorder state
          // The final chunk from stop() fires AFTER recorder becomes INACTIVE
          // We MUST process it or we lose the last part of the audio!
          if (!event.data || event.data.size === 0) {
            createLogger.log(
              "[ondataavailable RESUME] Ignoring empty chunk",
              recordingStartTimeRef.current
            );
            return;
          }

          if (!audioWorkerRef.current) {
            createLogger.error(
              "[ondataavailable RESUME] Audio worker not available, cannot process chunk",
              undefined,
              recordingStartTimeRef.current
            );
            return;
          }

          // Process the chunk - this is critical for the final chunk from stop()
          // The recorder may be INACTIVE when this fires, but we still need the data!
          const processingStartTime = Date.now();
          createLogger.log(
            `[ondataavailable RESUME] Processing chunk: ${chunkSize} bytes, recording duration: ${recordingDuration}`,
            recordingStartTimeRef.current
          );
          const chunkPromise = audioWorkerRef.current
            .addChunk(event.data)
            .then(() => {
              const processingElapsed = (
                (Date.now() - processingStartTime) /
                1000
              ).toFixed(3);
              const totalElapsed = (
                (Date.now() - chunkReceivedTime) /
                1000
              ).toFixed(3);
              createLogger.log(
                `[ondataavailable RESUME] Chunk saved successfully: ${chunkSize} bytes ` +
                  `(processing took: ${processingElapsed}s, total: ${totalElapsed}s, ` +
                  `recording duration: ${recordingDuration})`,
                recordingStartTimeRef.current
              );
            })
            .catch((error: unknown) => {
              // Log error but don't throw - individual chunk failures shouldn't stop recording
              createLogger.error(
                `[ondataavailable RESUME] Failed to add chunk to worker:`,
                error,
                recordingStartTimeRef.current
              );
            });

          // Track ALL chunks if we're stopping OR if recorder is inactive
          // This ensures we capture all final chunks, even if they arrive before isStoppingRef is set
          // or after stop() is called (when recorder becomes INACTIVE)
          // ALSO track if mediaRecorderRef is null (recorder was stopped/cleaned up)
          const shouldTrackAsFinal =
            isStoppingRef.current ||
            recorderState === MEDIA_RECORDER_STATES.INACTIVE ||
            !mediaRecorderRef.current;

          // ALWAYS track chunks during stop sequence - don't risk losing any data
          if (shouldTrackAsFinal) {
            finalChunkPromisesRef.current.push(chunkPromise);
            createLogger.log(
              `[ondataavailable RESUME] Tracking final chunk promise (${finalChunkPromisesRef.current.length} total) - ` +
                `isStopping=${isStoppingRef.current}, state=${recorderState}, ` +
                `recorderExists=${!!mediaRecorderRef.current}, recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );
          } else {
            // Even if not tracking as final, log it for debugging
            createLogger.log(
              `[ondataavailable RESUME] Chunk received but NOT tracking as final - ` +
                `isStopping=${isStoppingRef.current}, state=${recorderState}, ` +
                `recorderExists=${!!mediaRecorderRef.current}, recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );
          }

          // Check recording duration limit
          if (recordingStartTimeRef.current) {
            const elapsedTime = Date.now() - recordingStartTimeRef.current;
            const remainingTime = MAX_RECORDING_DURATION_MS - elapsedTime;
            if (remainingTime <= 0) {
              // Stop recording if max duration reached
              // Use void to prevent awaiting - this is fire-and-forget
              void stopRecording();
              dispatch(
                setCustomMessage(ERROR_MESSAGES.RECORDING_STOPPED_MAX_DURATION)
              );
            }
          }
        };

        mediaRecorder.onstop = async () => {
          // Save reference to recorder before cleanup (for safety)
          const currentRecorder = mediaRecorderRef.current;
          const onstopStartTime = Date.now();
          const recordingDuration = createLogger.getRecordingDuration(
            recordingStartTimeRef.current
          );

          try {
            createLogger.log(
              `[onstop RESUME] Recording stopped, processing... - Recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );

            // Ensure saving state is shown during chunk gathering
            // Dispatch immediately to show saving state in UI
            createLogger.log(
              "[onstop RESUME] Dispatching savingInProgressScribeRecording...",
              recordingStartTimeRef.current
            );
            dispatch(savingInProgressScribeRecording());
            const dispatchElapsed = (
              (Date.now() - onstopStartTime) /
              1000
            ).toFixed(3);
            createLogger.log(
              `[onstop RESUME] savingInProgressScribeRecording dispatched (elapsed: ${dispatchElapsed}s)`,
              recordingStartTimeRef.current
            );

            // Force a microtask to ensure React has processed the Redux state update
            await new Promise((resolve) => setTimeout(resolve, 0));

            // Wait for ALL async ondataavailable events to complete
            // We need to wait long enough for all events to fire and all chunks to be added to worker
            // The worker's waitForAllChunks() will then wait for all IndexedDB writes to complete
            createLogger.log(
              "[onstop RESUME] Waiting for all async chunk events to complete...",
              recordingStartTimeRef.current
            );

            // Wait for ALL chunks to be received from recorder and saved to IndexedDB
            // This uses polling to detect when chunks stabilize, then waits for all promises to complete
            createLogger.log(
              `[onstop RESUME] Initial chunk count: ${finalChunkPromisesRef.current.length}, recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );
            await waitForAllChunksToComplete();
            // Clear the ref after waiting (chunks are already saved to IndexedDB)
            finalChunkPromisesRef.current = [];

            // Step 1: Save recording in WebWorker
            // The worker will wait for all pending chunk operations internally via waitForAllChunks()
            // This is critical - must complete before cleanup

            // Verify chunks are actually in IndexedDB before reading
            // Add a small delay to ensure all IndexedDB transactions are fully committed
            await new Promise((resolve) => setTimeout(resolve, 200));

            // EXTRA SAFETY: Poll IndexedDB directly to verify chunks are actually saved
            await new Promise((resolve) => setTimeout(resolve, 100));

            const saveStartTime = Date.now();
            const saveElapsed = (
              (saveStartTime - onstopStartTime) /
              1000
            ).toFixed(3);
            createLogger.log(
              `[onstop RESUME] Calling saveRecording()... (elapsed: ${saveElapsed}s, recording duration: ${recordingDuration})`,
              recordingStartTimeRef.current
            );
            const result = await audioWorkerRef.current?.saveRecording();
            const saveCompletedElapsed = (
              (Date.now() - saveStartTime) /
              1000
            ).toFixed(3);
            const totalElapsed = (
              (Date.now() - onstopStartTime) /
              1000
            ).toFixed(3);
            const chunkCount = result?.chunks?.length || 0;
            const totalChunkSize =
              result?.chunks?.reduce(
                (sum: number, chunk: Blob) => sum + chunk.size,
                0
              ) || 0;

            createLogger.log(
              `[onstop RESUME] saveRecording() completed - chunks: ${chunkCount}, ` +
                `total size: ${totalChunkSize} bytes, ` +
                `avg chunk size: ${chunkCount > 0 ? Math.round(totalChunkSize / chunkCount) : 0} bytes, ` +
                `metadata: ${result?.metadata ? "yes" : "no"}, ` +
                `save took: ${saveCompletedElapsed}s, total elapsed: ${totalElapsed}s, ` +
                `recording duration: ${recordingDuration}`,
              recordingStartTimeRef.current
            );

            // WARNING: If chunk count seems low, log it for investigation
            if (chunkCount < 2) {
              createLogger.warn(
                `[onstop RESUME] WARNING: Only ${chunkCount} chunk(s) found! ` +
                  `Expected more chunks based on recording duration: ${recordingDuration}. ` +
                  `This might indicate missing audio data.`,
                recordingStartTimeRef.current
              );
            }

            // Log each chunk size for debugging
            if (result?.chunks && result.chunks.length > 0) {
              const chunkSizes = result.chunks.map(
                (chunk: Blob, idx: number) =>
                  `chunk${idx + 1}=${chunk.size} bytes`
              );
              createLogger.log(
                `[onstop RESUME] Chunk sizes: ${chunkSizes.join(", ")}, recording duration: ${recordingDuration}`,
                recordingStartTimeRef.current
              );
            }
            const { metadata, chunks, audioUrl } = result ?? {
              metadata: null,
              chunks: []
            };

            // Step 2: Update duration based on timestamps or chunk count
            if (metadata && chunks.length > 0) {
              await updateRecordingDurationFromChunks(
                metadata.id,
                chunks,
                recordingStartTimeRef.current
              );
            }

            // Step 3: Update UI state with audio data
            dispatch(stopScribeRecording(audioUrl));

            // Reset favicon to default when recording stops
            setFavicon(FAVICON_PATHS.DEFAULT);

            // Step 4: Cleanup sequence - must happen in correct order
            // Cleanup media resources (tracks, AudioContext) before clearing session
            audioRecordingCleanup();

            // Step 5: Clear recording session from IndexedDB
            await clearRecordingSession();

            // Step 6: Reset stopping flag (now safe to start new recording)
            isStoppingRef.current = false;

            // Step 7: Trigger upload queue processing
            // Use queueMicrotask to yield to UI before heavy upload operation
            queueMicrotask(() => {
              createLogger.log(
                "Triggering upload queue processing...",
                recordingStartTimeRef.current
              );
              if (uploadWorkerRef.current) {
                uploadWorkerRef.current
                  .processQueue()
                  .catch((error: unknown) => {
                    createLogger.error(
                      "Error processing upload queue:",
                      error,
                      recordingStartTimeRef.current
                    );
                  });
              } else {
                createLogger.error(
                  ERROR_MESSAGES.UPLOAD_WORKER_NOT_AVAILABLE,
                  undefined,
                  recordingStartTimeRef.current
                );
              }
            });
          } catch (error) {
            createLogger.error(
              "Error saving recording:",
              error,
              recordingStartTimeRef.current
            );

            // Ensure cleanup happens even on error
            // This prevents resource leaks
            try {
              audioRecordingCleanup();
              await clearRecordingSession();
            } catch (cleanupError) {
              console.error(
                "Error during cleanup after save failure:",
                cleanupError
              );
            }

            // Reset flag on error too
            isStoppingRef.current = false;

            // Reset favicon to default even on error
            setFavicon(FAVICON_PATHS.DEFAULT);

            // Dispatch error state to UI
            dispatch(stopScribeRecording(""));
          } finally {
            // Final safety check: ensure recorder ref is cleared
            // (should already be cleared by cleanup, but double-check)
            if (mediaRecorderRef.current === currentRecorder) {
              mediaRecorderRef.current = null;
            }
          }
        };
        recordingStartTimeRef.current = Date.now();

        mediaRecorder.start(CHUNK_TIMESLICE_MS);
        dispatch(
          startScribeRecording({
            patientId,
            chartNoteId: params.chartNoteId
          })
        );

        createLogger.log(
          "Recording resumed successfully with existing ID",
          recordingStartTimeRef.current
        );
      } catch (error) {
        createLogger.error(
          "Error resuming recording:",
          error,
          recordingStartTimeRef.current
        );
      }
    },
    [
      startAnalysis,
      audioRecordingCleanup,
      currentUser?.id,
      deviceSettings.microphone,
      dispatch,
      stopRecording,
      waitForAllChunksToComplete
    ]
  );

  const startRecording = useCallback(
    async (params: {
      patientId: number | undefined;
      patientFullName: string | undefined;
      chartNoteId: number | undefined;
      microphoneFingerprint?: MicrophoneInput;
    }) => {
      // Check if database is ready before starting recording
      if (!isDatabaseReadyRef.current) {
        createLogger.error(ERROR_MESSAGES.DATABASE_NOT_READY, undefined, null);
        return;
      }

      const { patientId, patientFullName } = params;
      const userId = currentUser?.id;
      const deviceId = deviceSettings.microphone;

      try {
        createLogger.log(
          `Starting recording for patient: ${patientFullName}`,
          recordingStartTimeRef.current
        );

        const constraints: MediaStreamConstraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        mediaStreamRef.current = stream;
        const suggestedBitrate = await detectSuggestedBitrate(stream);

        await startAnalysis(stream);

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: AUDIO_CONFIG.MIME_TYPE,
          audioBitsPerSecond: suggestedBitrate
        });
        mediaRecorderRef.current = mediaRecorder;

        // Clear any previous final chunk promises from previous recording
        finalChunkPromisesRef.current = [];

        const recordingId = await audioWorkerRef.current?.startRecording({
          patientId,
          userId,
          patientFullName
        });

        setFavicon(FAVICON_PATHS.RECORDING);

        mediaRecorder.ondataavailable = async (event) => {
          const recorderState = mediaRecorderRef.current?.state || "unknown";
          const chunkSize = event.data?.size || 0;
          const isStopping = isStoppingRef.current;

          createLogger.log(
            `[ondataavailable] Chunk received: size=${chunkSize} bytes, ` +
              `recorderState=${recorderState}, isStopping=${isStopping}, ` +
              `finalChunksTracked=${finalChunkPromisesRef.current.length}`,
            recordingStartTimeRef.current
          );

          // Process ALL chunks with data, regardless of recorder state
          // The final chunk from stop() fires AFTER recorder becomes INACTIVE
          // We MUST process it or we lose the last part of the audio!
          if (!event.data || event.data.size === 0) {
            createLogger.log(
              "[ondataavailable] Ignoring empty chunk",
              recordingStartTimeRef.current
            );
            return;
          }

          if (!audioWorkerRef.current) {
            createLogger.error(
              "[ondataavailable] Audio worker not available, cannot process chunk",
              undefined,
              recordingStartTimeRef.current
            );
            return;
          }

          // Process the chunk - this is critical for the final chunk from stop()
          // The recorder may be INACTIVE when this fires, but we still need the data!
          createLogger.log(
            `[ondataavailable] Processing chunk: ${chunkSize} bytes`,
            recordingStartTimeRef.current
          );
          const chunkPromise = audioWorkerRef.current
            .addChunk(event.data)
            .then(() => {
              createLogger.log(
                `[ondataavailable] Chunk saved successfully: ${chunkSize} bytes`,
                recordingStartTimeRef.current
              );
            })
            .catch((error: unknown) => {
              // Log error but don't throw - individual chunk failures shouldn't stop recording
              createLogger.error(
                `[ondataavailable] Failed to add chunk to worker:`,
                error,
                recordingStartTimeRef.current
              );
            });

          // Track ALL chunks if we're stopping OR if recorder is inactive
          // This ensures we capture all final chunks, even if they arrive before isStoppingRef is set
          // or after stop() is called (when recorder becomes INACTIVE)
          // ALSO track if mediaRecorderRef is null (recorder was stopped/cleaned up)
          const shouldTrackAsFinal =
            isStoppingRef.current ||
            recorderState === MEDIA_RECORDER_STATES.INACTIVE ||
            !mediaRecorderRef.current;

          // ALWAYS track chunks during stop sequence - don't risk losing any data
          if (shouldTrackAsFinal) {
            finalChunkPromisesRef.current.push(chunkPromise);
            createLogger.log(
              `[ondataavailable] Tracking final chunk promise (${finalChunkPromisesRef.current.length} total) - ` +
                `isStopping=${isStoppingRef.current}, state=${recorderState}, ` +
                `recorderExists=${!!mediaRecorderRef.current}`,
              recordingStartTimeRef.current
            );
          } else {
            // Even if not tracking as final, log it for debugging
            createLogger.log(
              `[ondataavailable] Chunk received but NOT tracking as final - ` +
                `isStopping=${isStoppingRef.current}, state=${recorderState}, ` +
                `recorderExists=${!!mediaRecorderRef.current}`,
              recordingStartTimeRef.current
            );
          }

          // Check recording duration limit
          if (recordingStartTimeRef.current) {
            const elapsedTime = Date.now() - recordingStartTimeRef.current;
            const remainingTime = MAX_RECORDING_DURATION_MS - elapsedTime;
            if (remainingTime <= 0) {
              // Stop recording if max duration reached
              // Use void to prevent awaiting - this is fire-and-forget
              void stopRecording();
              dispatch(
                setCustomMessage(ERROR_MESSAGES.RECORDING_STOPPED_MAX_DURATION)
              );
            }
          }
        };

        mediaRecorder.onstop = async () => {
          // Save reference to recorder before cleanup (for safety)
          const currentRecorder = mediaRecorderRef.current;

          try {
            createLogger.log(
              "Recording stopped, processing...",
              recordingStartTimeRef.current
            );

            // Ensure saving state is shown during chunk gathering
            // Dispatch immediately to show saving state in UI
            createLogger.log(
              "onstop: Dispatching savingInProgressScribeRecording...",
              recordingStartTimeRef.current
            );
            dispatch(savingInProgressScribeRecording());
            createLogger.log(
              "onstop: savingInProgressScribeRecording dispatched",
              recordingStartTimeRef.current
            );

            // Force a microtask to ensure React has processed the Redux state update
            await new Promise((resolve) => setTimeout(resolve, 0));

            // Wait for ALL chunks to be received from recorder and saved to IndexedDB
            // This uses polling to detect when chunks stabilize, then waits for all promises to complete
            createLogger.log(
              "Waiting for all async chunk events to complete...",
              recordingStartTimeRef.current
            );
            createLogger.log(
              `[onstop] Initial chunk count: ${finalChunkPromisesRef.current.length}`,
              recordingStartTimeRef.current
            );
            await waitForAllChunksToComplete();
            // Clear the ref after waiting (chunks are already saved to IndexedDB)
            finalChunkPromisesRef.current = [];

            // Step 1: Save recording in WebWorker
            // The worker will wait for all pending chunk operations internally
            // This is critical - must complete before cleanup

            // Verify chunks are actually in IndexedDB before reading
            // Add a small delay to ensure all IndexedDB transactions are fully committed
            await new Promise((resolve) => setTimeout(resolve, 200));

            // EXTRA SAFETY: Poll IndexedDB directly to verify chunks are actually saved
            // This helps catch any race conditions where chunks haven't been written yet
            if (audioWorkerRef.current && currentRecorder) {
              try {
                // Get the current recording ID from the worker (we need to access it)
                // Actually, we can't easily get it from here, so we'll rely on the worker's waitForAllChunks
                // But let's add one more verification step
                createLogger.log(
                  `[onstop] Final verification: Ensuring all IndexedDB writes are complete...`,
                  recordingStartTimeRef.current
                );
                await new Promise((resolve) => setTimeout(resolve, 100));
              } catch (error) {
                createLogger.warn(
                  `[onstop] Verification step error (non-critical):`,
                  recordingStartTimeRef.current
                );
              }
            }

            createLogger.log(
              `[onstop] Calling saveRecording()...`,
              recordingStartTimeRef.current
            );
            const result = await audioWorkerRef.current?.saveRecording();
            const chunkCount = result?.chunks?.length || 0;
            const totalChunkSize =
              result?.chunks?.reduce(
                (sum: number, chunk: Blob) => sum + chunk.size,
                0
              ) || 0;

            createLogger.log(
              `[onstop] saveRecording() completed - chunks: ${chunkCount}, ` +
                `total size: ${totalChunkSize} bytes, ` +
                `avg chunk size: ${chunkCount > 0 ? Math.round(totalChunkSize / chunkCount) : 0} bytes, ` +
                `metadata: ${result?.metadata ? "yes" : "no"}`,
              recordingStartTimeRef.current
            );

            // WARNING: If chunk count seems low, log it for investigation
            if (chunkCount < 2) {
              createLogger.warn(
                `[onstop] WARNING: Only ${chunkCount} chunk(s) found! ` +
                  `Expected more chunks based on recording duration. ` +
                  `This might indicate missing audio data.`,
                recordingStartTimeRef.current
              );
            }

            // Log each chunk size for debugging
            if (result?.chunks && result.chunks.length > 0) {
              const chunkSizes = result.chunks.map(
                (chunk: Blob, idx: number) =>
                  `chunk${idx + 1}=${chunk.size} bytes`
              );
              createLogger.log(
                `[onstop] Chunk sizes: ${chunkSizes.join(", ")}`,
                recordingStartTimeRef.current
              );
            }

            const { metadata, chunks } = result ?? {
              metadata: null,
              chunks: []
            };

            // Step 2: Update duration based on timestamps or chunk count
            if (metadata && chunks.length > 0) {
              await updateRecordingDurationFromChunks(
                metadata.id,
                chunks,
                recordingStartTimeRef.current
              );
            }

            // Step 3: Update UI state with audio data
            dispatch(stopScribeRecording(recordToDataUrl(chunks)));

            // Reset favicon to default when recording stops
            setFavicon(FAVICON_PATHS.DEFAULT);

            // Step 4: Cleanup sequence - must happen in correct order
            // Cleanup media resources (tracks, AudioContext) before clearing session
            audioRecordingCleanup();

            // Step 5: Clear recording session from IndexedDB
            await clearRecordingSession();

            // Step 6: Reset stopping flag (now safe to start new recording)
            isStoppingRef.current = false;

            // Step 7: Trigger upload queue processing
            // Use queueMicrotask to yield to UI before heavy upload operation
            queueMicrotask(() => {
              createLogger.log(
                "Triggering upload queue processing...",
                recordingStartTimeRef.current
              );
              if (uploadWorkerRef.current) {
                uploadWorkerRef.current
                  .processQueue()
                  .catch((error: unknown) => {
                    createLogger.error(
                      "Error processing upload queue:",
                      error,
                      recordingStartTimeRef.current
                    );
                  });
              } else {
                createLogger.error(
                  ERROR_MESSAGES.UPLOAD_WORKER_NOT_AVAILABLE,
                  undefined,
                  recordingStartTimeRef.current
                );
              }
            });
          } catch (error) {
            createLogger.error(
              "Error saving recording:",
              error,
              recordingStartTimeRef.current
            );

            // Ensure cleanup happens even on error
            // This prevents resource leaks
            try {
              audioRecordingCleanup();
              await clearRecordingSession();
            } catch (cleanupError) {
              console.error(
                "Error during cleanup after save failure:",
                cleanupError
              );
            }

            // Reset flag on error too
            isStoppingRef.current = false;

            // Reset favicon to default even on error
            setFavicon(FAVICON_PATHS.DEFAULT);

            // Dispatch error state to UI
            dispatch(stopScribeRecording(""));
          } finally {
            // Final safety check: ensure recorder ref is cleared
            // (should already be cleared by cleanup, but double-check)
            if (mediaRecorderRef.current === currentRecorder) {
              mediaRecorderRef.current = null;
            }
          }
        };
        recordingStartTimeRef.current = Date.now();

        mediaRecorder.start(CHUNK_TIMESLICE_MS);
        dispatch(
          startScribeRecording({
            patientId,
            chartNoteId: undefined
          })
        );

        // Save recording session to IndexedDB for recovery after page reload
        await saveRecordingSession({
          recordingId: recordingId || `recording_${Date.now()}`, // Use actual recording ID from audio worker
          patientId: patientId || 0,
          patientFullName,
          chartNoteId: params.chartNoteId,
          recordingState: RECORDING_STATE_IN_PROGRESS,
          startTime: recordingStartTimeRef.current,
          tabInstanceId: appInstanceId
        });

        // Broadcast to other tabs that recording started on this tab
        dispatchTabSyncMessage({
          action: TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB,
          emitterId: appInstanceId
        });

        createLogger.log(
          "Recording started successfully",
          recordingStartTimeRef.current
        );
      } catch (error) {
        createLogger.error(
          "Error accessing microphone:",
          error,
          recordingStartTimeRef.current
        );
      }
    },
    [
      startAnalysis,
      audioRecordingCleanup,
      currentUser?.id,
      deviceSettings.microphone,
      dispatch,
      stopRecording,
      waitForAllChunksToComplete
    ]
  );

  const pauseRecording = useCallback(async () => {
    createLogger.log("Pausing recording...", recordingStartTimeRef.current);
    if (mediaRecorderRef.current?.state === MEDIA_RECORDER_STATES.RECORDING) {
      // Request data before pausing to capture any buffered chunk
      // This ensures we don't lose the current chunk in progress
      createLogger.log(
        `[pauseRecording] Flushing buffered data before pause - current chunks tracked: ${finalChunkPromisesRef.current.length}`
      );
      mediaRecorderRef.current.requestData();

      // Wait longer for the chunk to be processed and saved
      await new Promise((resolve) => setTimeout(resolve, 300));
      createLogger.log(
        `[pauseRecording] After flush - chunks tracked: ${finalChunkPromisesRef.current.length}`,
        recordingStartTimeRef.current
      );

      mediaRecorderRef.current.pause();
      audioWorkerRef.current?.pauseRecording();
      dispatch(pauseScribeRecording());
      setFavicon(FAVICON_PATHS.PAUSED);
      await updateRecordingSessionState(RECORDING_STATE_PAUSED);

      // Broadcast to other tabs that recording paused on this tab
      dispatchTabSyncMessage({
        action: TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
        emitterId: appInstanceId
      });
    }
  }, [dispatch]);

  const resumeRecording = useCallback(async () => {
    createLogger.log("Resuming recording...", recordingStartTimeRef.current);

    // Check if we have an active MediaRecorder that can be resumed
    if (mediaRecorderRef.current?.state === MEDIA_RECORDER_STATES.PAUSED) {
      mediaRecorderRef.current.resume();
      audioWorkerRef.current?.resumeRecording();
      dispatch(resumeScribeRecording());
      setFavicon("/favicon-red.ico");
      await updateRecordingSessionState(RECORDING_STATE_IN_PROGRESS);

      // Broadcast to other tabs that recording resumed on this tab
      dispatchTabSyncMessage({
        action: TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB,
        emitterId: appInstanceId
      });

      return;
    }

    // If no active MediaRecorder (e.g., after page reload), check for persisted session
    try {
      const persistedSession = await getRecordingSession();
      if (
        persistedSession &&
        (persistedSession.recordingState === RECORDING_STATE_PAUSED ||
          persistedSession.recordingState === RECORDING_STATE_IN_PROGRESS)
      ) {
        createLogger.log(
          `Resuming from persisted session: recordingId=${persistedSession.recordingId}`,
          null
        );

        // Continue the existing recording session
        await startRecordingWithExistingId({
          patientId: persistedSession.patientId,
          patientFullName: persistedSession.patientFullName,
          chartNoteId: persistedSession.chartNoteId,
          existingRecordingId: persistedSession.recordingId
        });
      } else {
        createLogger.warn(
          ERROR_MESSAGES.NO_VALID_RECORDING_SESSION_TO_RESUME,
          null
        );
      }
    } catch (error) {
      createLogger.error(
        "Failed to resume from persisted session:",
        error,
        null
      );
    }
  }, [dispatch, startRecordingWithExistingId]);

  const checkFeatureAvailabilityAsync = useCheckPaywallFeatureAvailability();
  const [isAmbientScribeFeatureAvailable, setIsAmbientScribeFeatureAvailable] =
    useState(false);

  useEffect(() => {
    checkFeatureAvailabilityAsync(AMBIENT_SCRIBE_FEATURE_KEY).then(
      ({ isAvailable }) => {
        setIsAmbientScribeFeatureAvailable(isAvailable);
      }
    );
  }, [checkFeatureAvailabilityAsync]);

  const {
    requestCaptureVisit,
    isCaptureVisitLoading,
    openMicSetupModal,
    captureVisitModals
  } = useAmbientScribeCaptureVisit({
    startRecording,
    setIsAmbientScribeFeatureAvailable
  });

  const contextValue = useMemo(
    () => ({
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      resumePausedScribe: async () => ({ ok: false, reason: "start_failed" }),
      resetAfterPaused: () => {},
      prepareForResumeFromEditScribe: async () => {},
      mediaRecorderRef,
      editorRef,
      setEditorRef,
      insertScribeSelectionBookmarkRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable,
      requestCaptureVisit,
      isCaptureVisitLoading,
      isRecordingActiveOnAnotherTab,
      openMicSetupModal
    }),
    [
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      setEditorRef,
      isAmbientScribeFeatureAvailable,
      setIsAmbientScribeFeatureAvailable,
      requestCaptureVisit,
      isCaptureVisitLoading,
      isRecordingActiveOnAnotherTab,
      openMicSetupModal
    ]
  );

  const isChartNotesPage = useMemo(() => {
    const pathname = location.pathname;
    const chartNotesRegex = /^\/patients\/\d+(\/chart-notes\/?)?\/?$/;
    return chartNotesRegex.test(pathname);
  }, [location.pathname]);

  const isMedicalScribesPage = useMemo(() => {
    const pathname = location.pathname;
    return /^\/patients\/\d+\/medical-scribes\/?$/.test(pathname);
  }, [location.pathname]);

  const handleConfirmation = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const handleCancel = useCallback(() => {
    resumeRecording();
  }, [resumeRecording]);

  return (
    <MedicalScribeContext.Provider value={contextValue}>
      {children}
      {isMedicalScribeEnabled && !isRecordingActiveOnAnotherTab && (
        <MedicalScribeRecordingWidget
          mode={isChartNotesPage ? "fixed" : "draggable"}
          rightAnchorElementSelector=".chart-notes-buttons-container"
          isFullSize={isChartNotesPage || isMedicalScribesPage}
        />
      )}
      <BackgroundLoadingIndicator
        show={isUploading}
        title={UPLOAD_MESSAGES.UPLOADING_AUDIO_SCRIBE_DATA}
      />
      <MedicalScribeConfirmationDialog
        handlePauseRecording={pauseRecording}
        onConfirm={handleConfirmation}
        onCancel={handleCancel}
      />
      {captureVisitModals}
    </MedicalScribeContext.Provider>
  );
};

export const useMedicalScribe = (): MedicalScribeThinContextType => {
  const streamingContext = useContext(MedicalScribeStreamingContext);
  const legacyContext = useContext(MedicalScribeContext);

  if (streamingContext) {
    return streamingContext;
  }

  // Keep legacy provider compatibility while streaming remains the default.
  if (legacyContext) {
    return legacyContext;
  }

  throw new Error(
    "useMedicalScribe must be used within a MedicalScribeStreamingProvider (or legacy MedicalScribeProvider)"
  );
};
